import React, { useState } from "react";
import {
  Stack,
  Text,
  Link,
  mergeStyleSets,
  getTheme,
  PrimaryButton,
  DefaultButton,
} from "@fluentui/react";
import useAppInsights from "../../../Hooks/UseAppInsights";
import { NavType } from "../../../Shared/AppInsights";

interface RollbackDialogManualInstructionsProps {
  /**
   * Array of running rollout links with their associated environments.
   * These links will be displayed in step 1 for users to open and cancel.
   */
  latestRollouts: Array<{
    url: string;
    environment: string;
    isRunning: boolean;
  }>;
}

/**
 * A step-by-step wizard component that provides visual instructions for stopping
 * a currently running Ev2 rollout before performing a rollback. This component
 * blocks the user from proceeding until they complete the manual steps.
 */
const RollbackDialogManualInstructions: React.FC<
  RollbackDialogManualInstructionsProps
> = ({ latestRollouts }) => {
  const { trackOperationEvent, trackNavigationEvent } = useAppInsights(
    "RollbackDialogManualInstructions",
  );
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "Open Currently Running Ev2 Rollouts",
      content: (
        <Stack tokens={{ childrenGap: 8 }}>
          <Text>
            Within an <strong>AME</strong> account or <strong>SAW</strong>, open
            {latestRollouts.length === 1 ? " the" : " each of the"} currently
            running Ev2 rollout
            {latestRollouts.length > 1 ? "s" : ""}:
          </Text>
          <Stack
            tokens={{ childrenGap: 4 }}
            styles={{ root: { paddingLeft: 16 } }}
          >
            {latestRollouts
              .filter(({ isRunning }) => isRunning)
              .map((link) => (
                <Text key={link.environment}>
                  <strong>{link.environment}:</strong>{" "}
                  <Link
                    href={link.url}
                    target="_blank"
                    onClick={() => {
                      trackNavigationEvent({
                        destination: link.url,
                        navType: NavType.External,
                      });
                    }}
                  >
                    Open Ev2 Portal
                  </Link>
                </Text>
              ))}
          </Stack>
        </Stack>
      ),
      image: "rollbackDialog_step1.png",
    },
    {
      title:
        latestRollouts.length > 1
          ? "Cancel Each Rollout"
          : "Cancel the Rollout",
      content: (
        <Text>
          Locate and select the <strong>Cancel</strong> button in the Ev2 portal
          {latestRollouts.length > 1 ? " for each rollout" : ""}, then confirm
          the cancellation{latestRollouts.length > 1 ? "s" : ""}
        </Text>
      ),
      image: "rollbackDialog_step2.png",
    },
    {
      title: "Verify Cancellation Complete",
      content: (
        <Text>
          Wait for{" "}
          {latestRollouts.length > 1
            ? "all rollout cancellations"
            : "the rollout cancellation"}{" "}
          to complete. Once{" "}
          {latestRollouts.length > 1 ? "all rollouts are" : "the rollout is"} no
          longer running, you will be able to start a rollback. Close this
          dialog and try again.
        </Text>
      ),
      image: "rollbackDialog_step3.png",
    },
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
    trackOperationEvent({
      operation: "NextStep",
      function: "nextStep",
    });
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
    trackOperationEvent({
      operation: "PreviousStep",
      function: "prevStep",
    });
  };

  const goToStep = (stepIndex: number) => {
    setCurrentStep(stepIndex);
    trackOperationEvent({
      operation: "GoToStep",
      function: "goToStep",
      properties: {
        stepIndex,
      },
    });
  };

  return (
    <Stack tokens={{ childrenGap: 16 }} className={styles.stepContainer}>
      <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
        <div className={styles.stepNumber}>{currentStep + 1}</div>
        <Text variant="large" className={styles.stepTitle}>
          {steps[currentStep].title}
        </Text>
      </Stack>

      {/* Step Content */}
      <Stack tokens={{ childrenGap: 16 }}>
        {steps[currentStep].content}

        {/* Image */}
        <div className={styles.imageContainer}>
          <div className={styles.imagePlaceholder}>
            <img
              src={`/images/${steps[currentStep].image}`}
              alt={steps[currentStep].title}
              className={styles.stepImage}
            />
          </div>
        </div>
      </Stack>

      {/* Navigation Buttons */}
      <Stack
        horizontal
        tokens={{ childrenGap: 8 }}
        horizontalAlign="space-between"
      >
        <DefaultButton
          text="Previous"
          iconProps={{ iconName: "ChevronLeft" }}
          onClick={prevStep}
          disabled={currentStep === 0}
        />

        {/* Progress Indicators */}
        <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="center">
          {steps.map((_, index) => (
            <div
              key={index}
              className={index === currentStep ? styles.dotActive : styles.dot}
              onClick={() => goToStep(index)}
              role="button"
              tabIndex={0}
              aria-label="step"
            />
          ))}
        </Stack>

        {currentStep < steps.length - 1 ? (
          <PrimaryButton
            text="Next"
            iconProps={{ iconName: "ChevronRight" }}
            onClick={nextStep}
          />
        ) : (
          <div style={{ width: "80px" }} /> // Spacer to maintain layout
        )}
      </Stack>
    </Stack>
  );
};

const theme = getTheme();

const styles = mergeStyleSets({
  stepTitle: {
    fontWeight: 600,
  },
  stepImage: {
    width: "100%",
  },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    backgroundColor: theme.palette.themePrimary,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 600,
    flexShrink: 0,
  },
  stepContainer: {
    padding: "16px",
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: "4px",
    backgroundColor: theme.palette.neutralLighterAlt,
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    backgroundColor: theme.palette.neutralTertiary,
    cursor: "pointer",
    transition: "all 0.2s ease",
    ":hover": {
      backgroundColor: theme.palette.neutralSecondary,
    },
  },
  dotActive: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    backgroundColor: theme.palette.themePrimary,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  imageContainer: {
    width: "100%",
    maxWidth: "600px",
    alignSelf: "center",
    overflow: "hidden",
  },
  imagePlaceholder: {
    width: "100%",
    border: `2px dashed ${theme.palette.neutralTertiary}`,
    borderRadius: "4px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.neutralLighter,
    padding: 5,
    overflow: "hidden",
  },
});

export default RollbackDialogManualInstructions;
